/**
 * The rules of the /pricing page's "Back to top" button (kitecorp/transferify-ui#4043, owner decision 2026-11-02).
 * Plain TypeScript, so each one can be asserted without rendering anything; `useBackToTop` reads the browser and asks.
 */

/**
 * The widest viewport that gets the button: the owner's decision is "up to 960px wide".
 *
 * The rule reads the viewport's width. The comparison's own switch to two plans (`TWO_PLAN_LINE`) is a container query
 * on its section, and the two differ a little: where a browser draws a classic scrollbar, a window about 961px to 976px
 * wide has the two-plan table and no button.
 */
const WIDEST_VIEWPORT_PX = 960;

/** Air between the button's top edge and a footer link or a control a keyboard has scrolled to. */
const CLEARANCE_GAP_PX = 8;

export interface BackToTopReading {
	/** How far the page has scrolled. */
	scrollTop: number;
	/** The scroller's own height: half of this is the half a screen the decision names. */
	scrollerHeight: number;
	viewportWidth: number;
	/** The cookie banner is still waiting for an answer, and it covers this corner. */
	bannerUp: boolean;
}

/**
 * Whether the button is on the page. PAST half a screen rather than at it, which also keeps a scroller that has no
 * height yet, whose half is 0, from showing the button at the top of the page.
 */
export function showsBackToTop({scrollTop, scrollerHeight, viewportWidth, bannerUp}: BackToTopReading): boolean {
	return !bannerUp && viewportWidth <= WIDEST_VIEWPORT_PX && scrollTop > scrollerHeight / 2;
}

/** `instant` rather than `auto`: `auto` hands the choice back to the stylesheet, and this reader has already made it. */
export function backToTopScrollBehavior(reducedMotion: boolean): "smooth" | "instant" {
	return reducedMotion ? "instant" : "smooth";
}

/**
 * The room to keep free under whatever comes to rest near the bottom of the window, so it stops above the button.
 * From the button's measured top, so its inset, which tightens on a phone, is said once, in its stylesheet.
 */
export function backToTopClearance(viewportHeight: number, buttonTop: number): number {
	return Math.ceil(viewportHeight - buttonTop) + CLEARANCE_GAP_PX;
}
